/* =============================================================================
 * Atlas Market - alcohol + gift order controls
 *
 * WHAT THIS DOES
 *   - Shows/hides the gift panel when "This is a gift" is ticked.
 *   - Reveals the recipient 18+ confirmation only for alcohol gifts.
 *   - Keeps the add-to-cart button disabled until every required confirmation
 *     is ticked and every required gift field is filled.
 *   - Leaves unused fields `disabled` so they are never submitted as blank
 *     line item properties.
 *
 * WHAT THIS DOES *NOT* DO - READ BEFORE RELYING ON IT
 *   This is storefront JavaScript. It gates the ADD TO CART action only. It is
 *   NOT an enforcement boundary. A customer can still reach checkout with an
 *   alcohol line item that carries no confirmation by using: a cart permalink
 *   (/cart/{variant}:{qty}), direct navigation to /checkout, an accelerated
 *   checkout button (Shop Pay / Apple Pay / Google Pay / PayPal), an abandoned
 *   checkout recovery link, a draft order, or the Admin API.
 *
 *   Closing those routes requires a Cart and Checkout Validation Function,
 *   which on the Advanced plan must be delivered by a public Shopify App Store
 *   app. See the handover document for the full list.
 * ========================================================================== */

(function () {
  'use strict';

  var SCOPE_SELECTOR = 'product-form, form[data-type="add-to-cart-form"]';

  function scopeFor(el) {
    return el.closest(SCOPE_SELECTOR) || document;
  }

  function submitButtonIn(scope) {
    return scope.querySelector('button[name="add"], .product-form__submit');
  }

  function isAlcoholScope(scope) {
    return !!scope.querySelector('[data-atlas-confirm][data-atlas-scope="alcohol"]');
  }

  function giftToggleIn(scope) {
    return scope.querySelector('[data-atlas-gift-toggle]');
  }

  /* --- Enable/disable gift fields so blanks are never submitted ----------- */
  function syncGiftPanel(scope) {
    var toggle = giftToggleIn(scope);
    var panel = scope.querySelector('[data-atlas-gift-panel]');
    if (!toggle || !panel) return;

    var on = toggle.checked;
    panel.hidden = !on;

    scope.querySelectorAll('[data-atlas-gift-field]').forEach(function (field) {
      field.disabled = !on;
      if (!on) field.value = '';
    });

    /* The recipient 18+ confirmation only applies to alcohol gifts. */
    var recipientRow = scope.querySelector('[data-atlas-gift-only]');
    var recipientBox = scope.querySelector('[data-atlas-gift-required]');
    if (recipientRow && recipientBox) {
      var show = on && isAlcoholScope(scope);
      recipientRow.hidden = !show;
      recipientBox.disabled = !show;
      if (!show) recipientBox.checked = false;
    }
  }

  /* --- Gate the add-to-cart button ---------------------------------------- */
  function requirementsMet(scope) {
    var ok = true;

    scope.querySelectorAll('[data-atlas-required]').forEach(function (box) {
      if (!box.disabled && !box.checked) ok = false;
    });

    scope.querySelectorAll('[data-atlas-gift-required]').forEach(function (box) {
      if (!box.disabled && !box.checked) ok = false;
    });

    scope.querySelectorAll('[data-atlas-gift-required-field]').forEach(function (field) {
      if (!field.disabled && field.value.trim() === '') ok = false;
    });

    return ok;
  }

  function syncSubmitState(scope) {
    var button = submitButtonIn(scope);
    var fieldset = scope.querySelector('[data-atlas-confirm]');
    if (!button) return;

    var met = requirementsMet(scope);

    /* Never re-enable a button the theme disabled for its own reasons
       (sold out, unavailable variant, quantity rules). */
    if (button.dataset.atlasThemeDisabled === undefined) {
      button.dataset.atlasThemeDisabled = button.disabled ? 'true' : 'false';
    }
    if (button.dataset.atlasThemeDisabled === 'true') return;

    button.disabled = !met;
    button.setAttribute('aria-disabled', String(!met));

    if (fieldset && met) fieldset.classList.remove('is-invalid');
  }

  function flagInvalid(scope) {
    var fieldset = scope.querySelector('[data-atlas-confirm]');
    if (fieldset) fieldset.classList.add('is-invalid');
  }

  /* --- Wiring -------------------------------------------------------------- */
  function init(root) {
    var container = root || document;

    container.querySelectorAll('[data-atlas-gift], [data-atlas-confirm]').forEach(function (el) {
      var scope = scopeFor(el);
      if (scope.dataset && scope.dataset.atlasBound === 'true') return;
      if (scope.dataset) scope.dataset.atlasBound = 'true';

      syncGiftPanel(scope);
      syncSubmitState(scope);

      scope.addEventListener('change', function (event) {
        if (event.target.matches('[data-atlas-gift-toggle]')) syncGiftPanel(scope);
        syncSubmitState(scope);
      });

      scope.addEventListener('input', function () {
        syncSubmitState(scope);
      });

      /* Belt and braces: if the button is somehow clicked while unmet, stop it. */
      scope.addEventListener(
        'click',
        function (event) {
          var button = event.target.closest('button[name="add"], .product-form__submit');
          if (!button) return;
          if (!requirementsMet(scope)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            flagInvalid(scope);
          }
        },
        true
      );
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  /* Re-bind after variant changes / quick-add / section re-renders. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  if (window.MutationObserver) {
    new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) init(node);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }
})();
